import React, { createContext, useState, useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { deleteUser, patchUser } from '@/api/users/services'
import { postAuthLogin, postAuthLoginSso, postAuthRefresh, postAuthLogout } from '@/api/auth/services'
import { PostAuthLoginPayload, PostAuthLoginSsoPayload, PostAuthLogoutPayload, AuthUser } from '@/api/auth/types'
import { useQueryGetMe } from '@/api/users/queries'
import { useBackgroundLock } from '@/hooks/useBackgroundLock'
import { authUtils } from '@/utils/auth'
import { biometricUtils } from '@/utils/biometric'
import { setReauthHandler } from '@/utils/authReauthBridge'
import { QUERY_KEYS } from '@/constants/queryKeys'
import { User } from '@/types/User'
import { Logger } from '@/lib/logger'

const logger = new Logger('AuthContext')

type UpdateInfoPayload = Partial<Omit<AuthUser, 'id' | 'name' | 'email_verified'>> & {
  old_password?: string
  redirect_path?: string
}

interface AuthContextData {
  user: User | undefined
  isAuthenticated: boolean
  isLoading: boolean
  isLocked: boolean
  login: (username: string, password: string) => Promise<void>
  loginSso: (code: string) => Promise<void>
  loginWithBiometric: () => Promise<void>
  hasStoredRefresh: () => Promise<boolean>
  updateTokens: (access: string, refresh: string) => Promise<void>
  updateInfo: (newUser: UpdateInfoPayload) => Promise<void>
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>
  refreshUser: () => void
  deleteAccount: () => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextData | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const isAuthenticatedRef = useRef(false)
  const { data: user, isLoading: isLoadingUser } = useQueryGetMe({ enabled: isAuthenticated })

  const invalidateUser = useCallback(
    ({ keepAuthenticated = false }: { keepAuthenticated?: boolean } = {}) => {
      isAuthenticatedRef.current = keepAuthenticated
      setIsAuthenticated(keepAuthenticated)

      if (keepAuthenticated) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTH.GET_ME] })
      } else {
        queryClient.removeQueries({ queryKey: [QUERY_KEYS.AUTH.GET_ME] })
      }
    },
    [queryClient]
  )

  const performTokenRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const { refreshTokenStr } = await authUtils.getStoredTokens()
      if (!refreshTokenStr || !authUtils.isTokenValid(refreshTokenStr)) return false

      const response = await postAuthRefresh({ refresh: refreshTokenStr })
      await authUtils.setStoredTokens(response.access, response.refresh)
      return true
    } catch {
      return false
    }
  }, [])

  const attemptSilentRefresh = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticatedRef.current) return false
    return performTokenRefresh()
  }, [performTokenRefresh])

  const updateTokens = useCallback(
    async (access: string, refresh: string) => {
      await authUtils.setStoredTokens(access, refresh)
      invalidateUser({ keepAuthenticated: true })
      setIsLocked(false)
    },
    [invalidateUser]
  )

  const checkAuth = useCallback(async () => {
    try {
      const { accessToken, refreshTokenStr } = await authUtils.getStoredTokens()
      const hasValidAccess = !!accessToken && authUtils.isTokenValid(accessToken)
      const hasValidRefresh = !!refreshTokenStr && authUtils.isTokenValid(refreshTokenStr)
      const isBiometricEnabled = await biometricUtils.isEnabled()

      if (hasValidRefresh && isBiometricEnabled) {
        isAuthenticatedRef.current = true
        setIsAuthenticated(true)
        setIsLocked(true)
        return
      }

      if (hasValidAccess) {
        isAuthenticatedRef.current = true
        setIsAuthenticated(true)
        return
      }

      if (hasValidRefresh) {
        isAuthenticatedRef.current = false
        setIsAuthenticated(false)
        return
      }

      isAuthenticatedRef.current = false
      setIsAuthenticated(false)
    } catch {
      isAuthenticatedRef.current = false
      setIsAuthenticated(false)
      await authUtils.clearStoredTokens()
      invalidateUser()
    } finally {
      setIsLoading(false)
    }
  }, [invalidateUser])

  const login = useCallback(
    async (username: string, password: string) => {
      setIsLoading(true)
      await authUtils.clearStoredTokens()

      try {
        const payload: PostAuthLoginPayload = { username, password }
        const response = await postAuthLogin(payload)
        await updateTokens(response.access, response.refresh)
      } catch (error) {
        invalidateUser()
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [updateTokens, invalidateUser]
  )

  const loginWithBiometric = useCallback(async () => {
    setIsLoading(true)

    try {
      const reauthSuccess = await performTokenRefresh()
      if (!reauthSuccess) throw new Error('Sessão expirada. Faça login novamente.')
      invalidateUser({ keepAuthenticated: true })
      setIsLocked(false)
    } catch (error) {
      invalidateUser()
      await biometricUtils.clear()
      await authUtils.clearStoredTokens()
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [invalidateUser, performTokenRefresh])

  const hasStoredRefresh = useCallback(async () => {
    const { refreshTokenStr } = await authUtils.getStoredTokens()
    return !!refreshTokenStr && authUtils.isTokenValid(refreshTokenStr)
  }, [])

  const shouldLock = useCallback(async () => {
    const [biometricEnabled, hasRefresh] = await Promise.all([biometricUtils.isEnabled(), hasStoredRefresh()])
    return biometricEnabled && hasRefresh
  }, [hasStoredRefresh])

  const lock = useCallback(async () => {
    if (!isAuthenticatedRef.current) return
    if (await shouldLock()) setIsLocked(true)
  }, [shouldLock])

  const loginSso = useCallback(
    async (code: string) => {
      setIsLoading(true)
      await authUtils.clearStoredTokens()

      try {
        const payload: PostAuthLoginSsoPayload = { code }
        const response = await postAuthLoginSso(payload)
        await updateTokens(response.access, response.refresh)
      } catch (error) {
        invalidateUser()
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [invalidateUser, updateTokens]
  )

  const refreshUser = useCallback(() => {
    invalidateUser({ keepAuthenticated: true })
  }, [invalidateUser])

  const updateInfo = useCallback(
    async (newUser: UpdateInfoPayload) => {

      if (!user) {
        setIsLoading(false)
        throw new Error('Usuário não autenticado.')
      }

      await patchUser({
        id: user.id,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        cpf: newUser.cpf,
        phone: newUser.phone,
        email: newUser.email,
        old_password: newUser.old_password,
        redirect_path: newUser.redirect_path,
      })
      invalidateUser({ keepAuthenticated: true })
    },
    [user, invalidateUser]
  )

  const updatePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      setIsLoading(true)

      if (!user) {
        setIsLoading(false)
        throw new Error('Usuário não autenticado.')
      }

      try {
        await patchUser({
          id: user.id,
          old_password: currentPassword,
          password: newPassword,
        })
        invalidateUser({ keepAuthenticated: true })
      } finally {
        setIsLoading(false)
      }
    },
    [user, invalidateUser]
  )

  const logout = useCallback(async () => {
    setIsLoading(true)

    try {
      const { refreshTokenStr: refresh } = await authUtils.getStoredTokens()
      if (refresh) {
        const payload: PostAuthLogoutPayload = { refresh }
        await postAuthLogout(payload)
      }
    } catch (error) {
      logger.warn('Logout error', error)
    } finally {
      authUtils.clearStoredTokens()
      biometricUtils.clear()
      invalidateUser()
      setIsLocked(false)
      setIsLoading(false)
    }
  }, [invalidateUser])

  const deleteAccount = useCallback(async () => {
    setIsLoading(true)

    if (!user) {
      setIsLoading(false)
      throw new Error('Usuário não autenticado.')
    }

    try {
      await deleteUser({ id: user.id })
    } finally {
      authUtils.clearStoredTokens()
      biometricUtils.clear()
      invalidateUser()
      setIsLocked(false)
      setIsLoading(false)
    }
  }, [user, invalidateUser])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) setIsLocked(false)
  }, [isAuthenticated])

  useEffect(() => {
    setReauthHandler(attemptSilentRefresh)
    return () => setReauthHandler(null)
  }, [attemptSilentRefresh])

  useBackgroundLock(lock)

  useEffect(() => {
    const handleSessionExpired = () => invalidateUser()
    window.addEventListener('auth:session-expired', handleSessionExpired)
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired)
  }, [invalidateUser])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading: isLoading || isLoadingUser,
        isLocked,
        login,
        loginSso,
        loginWithBiometric,
        hasStoredRefresh,
        updateTokens,
        updateInfo,
        updatePassword,
        refreshUser,
        deleteAccount,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
